import React, { useRef, useEffect, useState } from 'react';
import {
  Card,
  Radio,
  Typography,
  Button,
  Row,
  Col,
  Empty,
  Spin,
  message,
  Table,
  Tag,
  Modal,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  BookOutlined,
  FileTextOutlined,
  TagsOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  ProfileOutlined,
} from '@ant-design/icons';
import {
  type OntologyDetail as OntologyDetailType,
  OntologyApi,
  type OntologyMount,
  type MountType,
} from '../api';
import { VersionApi, type OntologyVersionDetail } from '../api/versionApi';
import { useConfig } from '../hooks/useConfig';
import { useOPMDraft } from '../api/opmApi';
import { EditableField, EditableTags } from './EditableField';
import { useEditableField } from '../hooks/useOntologyDetail';
import { ResourceHelpers } from '../utils/resourceHelpers';
import KnowledgeGraph from './KnowledgeGraph';
import PublishProgressModal from './PublishProgressModal';
import { convertOPMToKnowledgeGraph } from '../utils/opmToKnowledgeGraph';
import {
  createNewNode,
  addNodeToCorrectPosition,
  addNodeToDefaultOrRoot,
  updateNodeTitle,
} from '../utils/resourceTreeUtils';
import { OntologyNode } from '../hooks/useResourceTree';
import SemanticDataMapping from '@/pages/SemanticDataMapping';
import OntologyPermissionPanel from './permissions/OntologyPermissionPanel';

const { Title, Text } = Typography;

interface OntologyDetailProps {
  ontologyDetail: OntologyDetailType | null;
  loadingDetail: boolean;
  onBack: () => void;
  loadOntologyDetail: (id: number) => Promise<void>;
  // 添加用于新建本体的属性
  onAddOntology?: () => void;
  ontologyList?: any[];
  setOntologyList?: React.Dispatch<React.SetStateAction<any[]>>;
  selectedNode?: any;
  expandedKeys?: React.Key[];
  setExpandedKeys?: (keys: React.Key[]) => void;
  setEditingKey?: (key: string | null) => void;
  setEditingValue?: (value: string) => void;
}

export const OntologyDetail: React.FC<OntologyDetailProps> = ({
  ontologyDetail,
  loadingDetail,
  onBack,
  loadOntologyDetail,
  // 解构新增的属性
  onAddOntology,
  ontologyList = [],
  setOntologyList,
  selectedNode,
  expandedKeys = [],
  setExpandedKeys,
  setEditingKey,
  setEditingValue,
}) => {
  const navigate = useNavigate();
  const editingRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState('basic'); // 添加活动标签状态

  // 删除版本状态管理
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletingVersion, setDeletingVersion] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 发布状态管理
  const [publishLoading, setPublishLoading] = useState(false);
  // 发布校验/进度弹窗
  const [publishModalOpen, setPublishModalOpen] = useState(false);

  // 版本查看状态管理
  const [viewVersionModalVisible, setViewVersionModalVisible] = useState(false);
  const [viewingVersionData, setViewingVersionData] =
    useState<OntologyVersionDetail | null>(null);
  const [viewVersionLoading, setViewVersionLoading] = useState(false);

  const [viewingVersionOplData, setViewingVersionOplData] = useState<
    string | null
  >(null);

  // 挂载数据状态管理
  const { globalConfig } = useConfig();
  const intellisightUrl =
    (globalConfig as any).intellisightUrl || 'INTELLIDO_DOMAIN';
  const [mountData, setMountData] = useState<OntologyMount[]>([]);
  const [mountLoading, setMountLoading] = useState(false);
  const [mountDetailModalVisible, setMountDetailModalVisible] = useState(false);
  const [currentMountType, setCurrentMountType] = useState<MountType | null>(
    null
  );

  // 根据挂载类型获取对应的挂载列表
  const getMountsByType = (type: MountType) => {
    return mountData.filter(item => item.mountType === type);
  };

  // 挂载数量统计
  const mountCounts = {
    'knowledge-base': getMountsByType('knowledge-base').length,
    'agent-chat': getMountsByType('agent-chat').length,
    workflow: getMountsByType('workflow').length,
  };

  // 获取挂载数据
  const fetchMountData = async (ontologyId: number) => {
    try {
      setMountLoading(true);
      const data = await OntologyApi.getOntologyMounts(ontologyId);
      setMountData(data || []);
    } catch (error) {
      console.error('获取挂载数据失败:', error);
      setMountData([]);
    } finally {
      setMountLoading(false);
    }
  };

  // 当本体切换时获取挂载数据
  useEffect(() => {
    if (ontologyDetail?.id) {
      fetchMountData(ontologyDetail.id);
    }
  }, [ontologyDetail?.id]);

  // 切换本体或失去管理权限时，若当前停留在权限管理 tab，重置为基础信息
  useEffect(() => {
    if (activeTab === 'permission' && !ontologyDetail?.canEdit) {
      setActiveTab('basic');
    }
  }, [ontologyDetail?.id, ontologyDetail?.canEdit, activeTab]);

  // 打开挂载详情弹窗
  const handleShowMountDetail = (type: MountType) => {
    setCurrentMountType(type);
    setMountDetailModalVisible(true);
  };

  // 跳转到应用详情
  const handleViewApp = (appId: string) => {
    window.open(`${intellisightUrl}/app/${appId}/configuration`, '_blank');
  };

  const {
    editingField,
    setEditingField,
    tempEditValue,
    setTempEditValue,
    tempTagValue,
    setTempTagValue,
    handleEditField,
    handleSaveField,
    handleCancelFieldEdit,
    handleAddTag,
    handleRemoveTag,
  } = useEditableField(
    ontologyDetail,
    loadOntologyDetail,
    // 名称更新后同步左侧树列表
    (newName: string) => {
      if (!selectedNode?.key || !setOntologyList) return;
      setOntologyList((prev: OntologyNode[]) =>
        updateNodeTitle(prev, selectedNode.key, newName)
      );
    }
  );

  // 点击外部区域退出编辑模式
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        editingField &&
        editingRef.current &&
        !editingRef.current.contains(event.target as Node)
      ) {
        setEditingField(null);
        setTempEditValue('');
        setTempTagValue('');
      }
    };

    if (editingField) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingField, setEditingField, setTempEditValue, setTempTagValue]);

  // 处理查看版本
  const handleViewVersion = async (version: any) => {
    try {
      setViewVersionLoading(true);
      // 同时获取图谱数据和OPL文本数据
      const [versionDetail, versionOplText] = await Promise.all([
        VersionApi.getOntologyVersionDetail(version.id),
        VersionApi.getOntologyVersionOpl(version.id),
      ]);

      // 检查是否有图谱数据
      if (
        versionDetail.graph &&
        (versionDetail.graph.nodes?.length || versionDetail.graph.edges?.length)
      ) {
        setViewingVersionData(versionDetail);
        setViewingVersionOplData(versionOplText);
        setViewVersionModalVisible(true);
      } else {
        message.info('该版本暂无图谱数据');
      }
    } catch (error: any) {
      message.error(`获取版本数据失败：${error.message || '未知错误'}`);
    } finally {
      setViewVersionLoading(false);
    }
  };

  // 关闭版本查看弹窗
  const handleCloseViewVersionModal = () => {
    setViewVersionModalVisible(false);
    setViewingVersionData(null);
    setViewingVersionOplData(null);
  };

  // 处理删除版本
  const handleDeleteVersion = (version: any) => {
    // 检查版本状态，草稿和已发布版本不能删除
    if (version.status === 'DRAFT' || version.status === 'PUBLISHED') {
      message.warning('草稿版本和已发布版本不能被删除');
      return;
    }

    setDeletingVersion(version);
    setDeleteModalVisible(true);
  };

  // 确认删除版本
  const confirmDeleteVersion = async () => {
    if (!deletingVersion) return;

    try {
      setDeleteLoading(true);
      await VersionApi.deleteOntologyVersion(deletingVersion.id);

      message.success('版本删除成功');

      // 重新加载本体详情数据
      if (ontologyDetail) {
        await loadOntologyDetail(ontologyDetail.id);
      }

      setDeleteModalVisible(false);
      setDeletingVersion(null);
    } catch (error: any) {
      message.error(`删除失败：${error.message || '未知错误'}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  // 取消删除
  const cancelDeleteVersion = () => {
    setDeleteModalVisible(false);
    setDeletingVersion(null);
  };

  // 处理重新发布版本
  const handleRepublishVersion = async (version: any) => {
    if (!ontologyDetail) return;

    try {
      setPublishLoading(true);
      await VersionApi.republishOntologyVersion(version.id);

      message.success('版本重新发布成功');

      // 重新加载本体详情
      await loadOntologyDetail(ontologyDetail.id);
    } catch (error: any) {
      message.error(`重新发布失败：${error.message || '未知错误'}`);
    } finally {
      setPublishLoading(false);
    }
  };

  // 处理发布版本
  const handlePublishVersion = async (version: any) => {
    if (!ontologyDetail) return;

    try {
      setPublishLoading(true);
      await VersionApi.publishOntologyVersion(version.id);

      message.success('版本发布成功');

      // 重新加载本体详情
      await loadOntologyDetail(ontologyDetail.id);
    } catch (error: any) {
      message.error(`发布失败：${error.message || '未知错误'}`);
    } finally {
      setPublishLoading(false);
    }
  };

  // 处理下线版本
  const handleUnpublishVersion = async (version: any) => {
    if (!ontologyDetail) return;

    Modal.confirm({
      title: '确认下线版本',
      content: `确定要下线版本 "${version.versionName}" 吗？`,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          setPublishLoading(true);
          await VersionApi.unpublishOntologyVersion(version.id);

          message.success('版本下线成功');

          // 重新加载本体详情
          await loadOntologyDetail(ontologyDetail.id);
        } catch (error: any) {
          message.error(`下线失败：${error.message || '未知错误'}`);
        } finally {
          setPublishLoading(false);
        }
      },
    });
  };

  // 处理编辑版本
  const handleEditVersion = (version: any) => {
    if (!ontologyDetail) return;

    Modal.confirm({
      title: '编辑提醒',
      content: `当前为历史版本，仅支持查看，无法直接编辑，是否继续？`,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          setPublishLoading(true);
          await VersionApi.editOntologyVersion(version.id, {
            versionName: version.versionName,
            versionDescription: version.versionDescription,
          });

          // message.success('进入编辑模式');
          navigate(`/opm-editor?id=${ontologyDetail.id}`);
        } catch (error: any) {
          message.error(`编辑失败：${error.message || '未知错误'}`);
        } finally {
          setPublishLoading(false);
        }
      },
    });
  };

  // 处理新建本体功能
  const handleAddOntology = () => {
    // 如果传入了外部的添加本体函数，则优先调用它
    if (onAddOntology) {
      onAddOntology();
      return;
    }

    // 如果没有传入外部函数但传入了必要的属性，则使用默认逻辑
    if (setOntologyList && setEditingKey && setEditingValue) {
      // 使用工具函数创建新节点
      const newNode = createNewNode(
        'ontology',
        selectedNode as OntologyNode | null,
        ontologyList as OntologyNode[]
      );

      // 将新本体添加到正确的位置
      if (selectedNode) {
        // 有选中的节点，添加到正确位置
        setOntologyList((prev: OntologyNode[]) =>
          addNodeToCorrectPosition(
            prev,
            newNode,
            selectedNode as OntologyNode,
            ontologyList as OntologyNode[]
          )
        );
        // 确保父目录展开
        if (
          setExpandedKeys &&
          selectedNode.key &&
          !expandedKeys.includes(selectedNode.key)
        ) {
          setExpandedKeys([...expandedKeys, selectedNode.key]);
        }
      } else {
        // 没有选中节点，添加到默认文件夹下或根级别
        setOntologyList((prev: OntologyNode[]) =>
          addNodeToDefaultOrRoot(prev, newNode, expandedKeys, setExpandedKeys)
        );
      }

      setEditingKey(newNode.key);
      setEditingValue('');
    } else {
      // 如果没有传入必要的属性，显示提示信息
      message.info('新建本体功能需要在父组件中配置');
    }
  };

  /** 点击"发布"：打开弹窗，由弹窗内部完成校验 → 表单 → 同步发布的所有步骤 */
  const handlePublish = () => {
    if (!ontologyDetail) return;
    setPublishModalOpen(true);
  };

  // 发布弹窗关闭
  const handleProgressModalClose = () => {
    setPublishModalOpen(false);
    if (ontologyDetail?.id) {
      loadOntologyDetail(ontologyDetail.id);
    }
  };

  // 发布成功回调
  const handlePublishComplete = () => {
    if (ontologyDetail?.id) {
      loadOntologyDetail(ontologyDetail.id);
    }
  };

  // 处理下线本体
  const handleUnpublishOntology = async () => {
    if (!ontologyDetail) return;

    try {
      setPublishLoading(true);
      await OntologyApi.unpublishOntology(ontologyDetail.id);

      message.success('本体下线成功');

      // 重新加载本体详情数据
      await loadOntologyDetail(ontologyDetail.id);
    } catch (error: any) {
      message.error(`下线失败：${error.message || '未知错误'}`);
    } finally {
      setPublishLoading(false);
    }
  };

  // 版本列表表格列定义
  const versionColumns = [
    {
      title: '版本名称',
      dataIndex: 'versionName',
      key: 'versionName',
      render: (text: string, record: any) => (
        <div className="flex items-center space-x-2">
          <span className="font-medium">{text}</span>
          {record.status === 'PUBLISHED' && <Tag color="green">已发布</Tag>}
          {record.status === 'DRAFT' && <Tag color="orange">草稿</Tag>}
        </div>
      ),
    },
    {
      title: '修改时间',
      dataIndex: 'updatedTime',
      key: 'updatedTime',
      render: (text: string) => ResourceHelpers.formatDateTime(text),
    },
    {
      title: '发布人', //'更新人',
      dataIndex: 'publishUser', // 'updateUser',
      key: 'publishUser', // 'updateUser',
    },
    {
      title: '描述',
      dataIndex: 'versionDescription',
      key: 'versionDescription',
      render: (text: string) => text || '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (text: any, record: any) => (
        <div className="flex items-center space-x-2">
          {/* 有编辑权限显示"编辑"，仅查看权限显示"查看本体" */}
          {ontologyDetail?.canEdit ? (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              loading={publishLoading}
              onClick={() => handleEditVersion(record)}
            >
              编辑
            </Button>
          ) : (''
           
          )}

          {/* 以下操作需要编辑权限 */}
          {ontologyDetail?.canEdit && (
            <>
              {/* 已发布状态显示下线按钮 */}
              {record.status === 'PUBLISHED' && (
                <Button
                  type="link"
                  size="small"
                  danger
                  loading={publishLoading}
                  onClick={() => handleUnpublishVersion(record)}
                >
                  下线
                </Button>
              )}

              {/* 草稿状态显示发布按钮 */}
              {record.status === 'DRAFT' && (
                <Button
                  type="link"
                  size="small"
                  loading={publishLoading}
                  onClick={() => handlePublishVersion(record)}
                >
                  发布
                </Button>
              )}

              {/* 其他状态显示上线按钮 */}
              {record.status !== 'PUBLISHED' && record.status !== 'DRAFT' && (
                <Button
                  type="link"
                  size="small"
                  loading={publishLoading}
                  onClick={() => handleRepublishVersion(record)}
                >
                  上线
                </Button>
              )}
            </>
          )}

          {/* 删除按钮：需要管理权限，已发布状态置灰禁用 */}
          {ontologyDetail?.canManage && (
            <Button
              type="link"
              size="small"
              icon={<DeleteOutlined />}
              danger
              disabled={record.status === 'PUBLISHED'}
              onClick={() => handleDeleteVersion(record)}
            >
              删除
            </Button>
          )}
        </div>
      ),
    },
  ];

  // 渲染版本信息内容
  const renderVersionContent = () => {
    const versions = ontologyDetail?.versions || [];

    return (
      <div className="mt-4">
        <Table
          columns={versionColumns}
          dataSource={versions}
          rowKey="id"
          pagination={{ pageSize: 5 }}
          locale={{
            emptyText: '暂无版本数据',
          }}
        />
      </div>
    );
  };

  if (loadingDetail) {
    return (
      <div className="p-6 bg-gray-50 min-h-full flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!ontologyDetail) {
    return (
      <div className="p-6 bg-gray-50 min-h-full flex items-center justify-center">
        <Empty description="未找到本体详情" />
      </div>
    );
  }
console.log('用户配置了本体的编辑权限', ontologyDetail, activeTab);

  return (
    <div className="h-full min-h-0 flex flex-col bg-gray-50">
      {/* 返回按钮 */}
      <div className="shrink-0 flex items-center justify-between px-6 pt-6 pb-4">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          className="mb-4"
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddOntology}
        >
          新建本体
        </Button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
        {/* 本体基本信息卡片 */}
        <Card
          className="mb-6 shadow-sm"
          title={
            <div className="flex items-center">
              <span>{ontologyDetail.name}</span>
              <div className="flex items-center ml-3">
                <div
                  className={`w-2 h-2 ${ResourceHelpers.getStatusColor(ontologyDetail.status)} rounded-full mr-1`}
                ></div>
                <span className="text-gray-400 text-sm">
                  {ResourceHelpers.getStatusDisplay(ontologyDetail.status)}
                </span>
              </div>
            </div>
          }
          extra={
            <div>
              {ontologyDetail.canEdit && ontologyDetail.status === 'PUBLISHED' && (
                <Button
                  type="primary"
                  danger
                  size="small"
                  className="mr-2"
                  loading={publishLoading}
                  onClick={handleUnpublishOntology}
                >
                  下线
                </Button>
              )}
              {ontologyDetail.canEdit && (
                <Button
                  type="primary"
                  size="small"
                  className="mr-2"
                  loading={publishLoading}
                  onClick={handlePublish}
                >
                  发布
                </Button>
              )}
              { (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    navigate(`/opm-editor?id=${ontologyDetail.id}`);
                  }}
                >
                  {ontologyDetail?.canEdit ? '编辑本体' : '查看本体'}
                </Button>
              )}
            </div>
          }
        >
          <div>
            <div className="space-y-4">
              {/* 按钮组 */}
              <Radio.Group
                value={activeTab}
                onChange={e => setActiveTab(e.target.value)}
                size="large"
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button
                  value="basic"
                  style={{
                    borderRadius: '6px',
                    border: 'none',
                    height: '40px',
                    paddingLeft: '20px',
                    paddingRight: '20px',
                  }}
                >
                  基础信息
                </Radio.Button>
                <Radio.Button
                  value="version"
                  style={{
                    borderRadius: '6px',
                    border: 'none',
                    height: '40px',
                    paddingLeft: '20px',
                    paddingRight: '20px',
                  }}
                >
                  版本信息
                </Radio.Button>
                {ontologyDetail.canEdit && (
                  <Radio.Button
                    value="permission"
                    style={{
                      borderRadius: '6px',
                      border: 'none',
                      height: '40px',
                      paddingLeft: '20px',
                      paddingRight: '20px',
                    }}
                  >
                    权限管理
                  </Radio.Button>
                )}
              </Radio.Group>

              {/* 内容区域 */}
              {activeTab === 'basic' ? (
                /* 基础信息内容 */
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <div className="space-y-3" ref={editingRef}>
                      {/* 名称 - 使用原来定义的交互方式 */}
                      <div
                        className="flex items-center p-3 rounded-lg"
                        style={{ backgroundColor: '#EBF7FF' }}
                      >
                        <div
                          className="flex items-center mr-4"
                          style={{ width: '140px' }}
                        >
                          <ProfileOutlined className="text-blue-500 mr-2" />
                          <span className="font-medium text-gray-800">
                            名称
                          </span>
                        </div>
                        <EditableField
                          fieldKey="name"
                          value={ontologyDetail.name || ''}
                          isTextArea={true}
                          isEditing={editingField === 'name'}
                          tempValue={tempEditValue}
                          onEdit={handleEditField}
                          onSave={handleSaveField}
                          onCancel={handleCancelFieldEdit}
                          onTempValueChange={setTempEditValue}
                          disabled={!ontologyDetail.canEdit}
                        />
                      </div>

                      {/* 定义字段已隐藏 - 不再维护
                      <div
                        className="flex items-center p-3 rounded-lg"
                        style={{ backgroundColor: '#EBF7FF' }}
                      >
                        <div
                          className="flex items-center mr-4"
                          style={{ width: '140px' }}
                        >
                          <BookOutlined className="text-blue-500 mr-2" />
                          <span className="font-medium text-gray-800">
                            定义
                          </span>
                        </div>
                        <EditableField
                          fieldKey="definition"
                          value={ontologyDetail.definition || ''}
                          isTextArea={true}
                          isEditing={editingField === 'definition'}
                          tempValue={tempEditValue}
                          onEdit={handleEditField}
                          onSave={handleSaveField}
                          onCancel={handleCancelFieldEdit}
                          onTempValueChange={setTempEditValue}
                        />
                      </div>
                      */}

                      {/* 当前的版本 */}
                      <div
                        className="flex items-center p-3 rounded-lg"
                        style={{ backgroundColor: '#EBF7FF' }}
                      >
                        <div
                          className="flex items-center mr-4"
                          style={{ width: '140px' }}
                        >
                          <InfoCircleOutlined className="text-blue-500 mr-2" />
                          <span className="font-medium text-gray-800">
                            当前活跃版本
                          </span>
                        </div>
                        <div className="flex items-center space-x-2 flex-1">
                          <span className="flex-1 text-gray-600 text-sm px-2 py-1">
                            {ontologyDetail.currentVersionId &&
                            ontologyDetail.versions
                              ? ontologyDetail.versions.find(
                                  v => v.id === ontologyDetail.currentVersionId
                                )?.versionName || '-'
                              : '-'}
                          </span>
                        </div>
                      </div>

                      {/* 版本更新时间 */}
                      <div
                        className="flex items-center p-3 rounded-lg"
                        style={{ backgroundColor: '#EBF7FF' }}
                      >
                        <div
                          className="flex items-center mr-4"
                          style={{ width: '140px' }}
                        >
                          <ClockCircleOutlined className="text-blue-500 mr-2" />
                          <span className="font-medium text-gray-800">
                            版本更新时间
                          </span>
                        </div>
                        <div className="flex items-center space-x-2 flex-1">
                          <span className="flex-1 text-gray-600 text-sm px-2 py-1">
                            {ResourceHelpers.formatDateTime(
                              ontologyDetail.updatedTime
                            )}
                          </span>
                        </div>
                      </div>

                      {/* 描述 */}
                      <div
                        className="flex items-center p-3 rounded-lg"
                        style={{ backgroundColor: '#EBF7FF' }}
                      >
                        <div
                          className="flex items-center mr-4"
                          style={{ width: '140px' }}
                        >
                          <FileTextOutlined className="text-blue-500 mr-2" />
                          <span className="font-medium text-gray-800">
                            描述
                          </span>
                        </div>
                        <EditableField
                          fieldKey="description"
                          value={ontologyDetail.description || ''}
                          isTextArea={true}
                          isEditing={editingField === 'description'}
                          tempValue={tempEditValue}
                          onEdit={handleEditField}
                          onSave={handleSaveField}
                          onCancel={handleCancelFieldEdit}
                          onTempValueChange={setTempEditValue}
                          disabled={!ontologyDetail.canEdit}
                        />
                      </div>

                      {/* 标签 */}
                      <div
                        className="flex items-center p-3 rounded-lg"
                        style={{ backgroundColor: '#EBF7FF' }}
                      >
                        <div
                          className="flex items-center mr-4"
                          style={{ width: '140px' }}
                        >
                          <TagsOutlined className="text-blue-500 mr-2" />
                          <span className="font-medium text-gray-800">
                            标签
                          </span>
                        </div>
                        <EditableTags
                          tags={ontologyDetail.tags || []}
                          isEditing={editingField === 'tags'}
                          tempTagValue={tempTagValue}
                          onEdit={() => setEditingField('tags')}
                          onAddTag={handleAddTag}
                          onRemoveTag={handleRemoveTag}
                          onCancel={() => setEditingField(null)}
                          onTempTagValueChange={setTempTagValue}
                          disabled={!ontologyDetail.canEdit}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
              ) : activeTab === 'version' ? (
                /* 版本信息内容 */
                renderVersionContent()
              ) : activeTab === 'permission' && ontologyDetail.canEdit ? (
                /* 权限管理内容 */
                <OntologyPermissionPanel ontologyId={ontologyDetail.id} />
              ) : null}
            </div>
          </div>
        </Card>

        {/* 统计信息卡片 */}
        <Card className="mb-6 shadow-sm" title={<div>统计信息</div>}>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <div
                style={{
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  background:
                    'linear-gradient(to right, #D4ECFF, #F6EDFF, #DCFEF2)',
                }}
                className="rounded-lg p-4 text-center"
              >
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-gray-600 text-sm mb-2">
                      对象数量
                    </div>
                    <div className="border-t border-gray-200 pt-1">
                      <span className="text-xl font-bold">
                        {ontologyDetail.objectCount}
                      </span>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-600 text-sm mb-2">
                      关系数量
                    </div>
                    <div className="border-t border-gray-200 pt-1">
                      <span className="text-xl font-bold">
                        {ontologyDetail.relationCount}
                      </span>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-600 text-sm mb-2">
                      过程数量
                    </div>
                    <div className="border-t border-gray-200 pt-1">
                      <span className="text-xl font-bold">
                        {ontologyDetail.processCount}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            {[
              {
                color: '#68CD7B',
                title: '挂载的知识库数量',
                value: mountCounts['knowledge-base'],
                type: 'knowledge-base' as MountType,
              },
              {
                color: '#FFD447',
                title: '挂载的Agent数量',
                value: mountCounts['agent-chat'],
                type: 'agent-chat' as MountType,
              },
              {
                color: '#EE5396',
                title: '挂载的场景数量',
                value: mountCounts['workflow'],
                type: 'workflow' as MountType,
              },
            ].map((item, index) => (
              <Col span={6} key={index}>
                <div
                  className="bg-white rounded-lg shadow-lg py-8 px-4 border-l-4 flex items-center justify-between"
                  style={{
                    borderLeftWidth: '5px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    borderLeft: `${item.color} solid`,
                  }}
                >
                  <div className="flex-1">
                    <div className="text-gray-800 font-medium flex items-center justify-left">
                      <div>{item.title}</div>
                      <div className="ml-5 text-xl">
                        {mountLoading ? '-' : item.value}
                      </div>
                    </div>
                  </div>
                  <div className="ml-4">
                    <Button
                      type="link"
                      size="small"
                      className="text-blue-500 hover:text-blue-600"
                      onClick={() => handleShowMountDetail(item.type)}
                    >
                      查看详情
                    </Button>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Card>

        <SemanticDataMapping ontologyId={ontologyDetail.id} />
        {/* 本体结构展示 */}
        {/* <Card title="预览" className="shadow-sm">
        <OntologyPreview ontologyId={ontologyDetail.id} />
      </Card> */}
      </div>

      {/* 删除版本确认弹窗 */}
      <Modal
        title="确认删除"
        open={deleteModalVisible}
        onOk={confirmDeleteVersion}
        onCancel={cancelDeleteVersion}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{
          danger: true,
          loading: deleteLoading,
        }}
        cancelButtonProps={{
          disabled: deleteLoading,
        }}
      >
        <p>确定要删除版本 "{deletingVersion?.versionName}" 吗？</p>
        <p className="text-red-500 text-sm">此操作不可撤销，请谨慎操作。</p>
      </Modal>

      {/* 发布弹窗：由内部完成校验 → 表单 → 同步发布的全流程 */}
      <PublishProgressModal
        ontologyId={ontologyDetail?.id || 0}
        open={publishModalOpen}
        onClose={handleProgressModalClose}
        onPublishComplete={handlePublishComplete}
      />

      {/* 版本查看弹窗 */}
      <Modal
        title={`查看版本: ${viewingVersionData?.versionName || ''}`}
        open={viewVersionModalVisible}
        onCancel={handleCloseViewVersionModal}
        footer={[
          <Button key="close" onClick={handleCloseViewVersionModal}>
            关闭
          </Button>,
        ]}
        width={1000}
        style={{ top: 20 }}
      >
        {viewVersionLoading ? (
          <div className="h-96 flex items-center justify-center">
            <Spin size="large" />
            <span className="ml-2">加载版本数据中...</span>
          </div>
        ) : (
          <div className="flex h-[60vh]">
            {/* OPL文本显示区域 - 30%宽度 */}
            <div className="w-3/12 mr-4 overflow-auto border border-gray-200 rounded">
              <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                <span className="font-medium">OPL文本</span>
              </div>
              <div className="p-4 h-full overflow-auto">
                {viewingVersionOplData ? (
                  <pre className="whitespace-pre-wrap text-sm">
                    {viewingVersionOplData}
                  </pre>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-500">
                    <Empty description="暂无OPL文本数据" />
                  </div>
                )}
              </div>
            </div>

            {/* 知识图谱显示区域 - 70%宽度 */}
            <div className="w-9/12 border border-gray-200 rounded">
              <div className="p-4 h-full">
                {viewingVersionData?.graph &&
                (viewingVersionData.graph.nodes?.length ||
                  viewingVersionData.graph.edges?.length) ? (
                  <div className="h-full w-full">
                    <KnowledgeGraph
                      key={`version-${viewingVersionData.id}`}
                      externalData={convertOPMToKnowledgeGraph({
                        nodes: viewingVersionData.graph.nodes || [],
                        edges: viewingVersionData.graph.edges || [],
                      })}
                      debug={false}
                    />
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-500">
                    <Empty description="该版本暂无图谱数据" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* 挂载详情弹窗 */}
      <Modal
        title={
          currentMountType === 'knowledge-base'
            ? '挂载的知识库'
            : currentMountType === 'agent-chat'
              ? '挂载的Agent'
              : '挂载的场景'
        }
        open={mountDetailModalVisible}
        onCancel={() => setMountDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setMountDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={600}
      >
        {currentMountType && (
          <div className="max-h-96 overflow-auto">
            {getMountsByType(currentMountType).length > 0 ? (
              <div className="space-y-2">
                {getMountsByType(currentMountType).map(item => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                  >
                    <span className="font-medium text-gray-800">
                      {item.name}
                    </span>
                    <Button
                      type="link"
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => handleViewApp(item.appId)}
                    >
                      查看
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <Empty description="暂无数据" />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

// 本体预览组件，用于在OntologyDetail中展示知识图谱
const OntologyPreview: React.FC<{ ontologyId: number }> = ({ ontologyId }) => {
  // 使用useOPMDraft hook获取本体草稿数据
  const {
    data: draftResponse,
    isLoading,
    error,
  } = useOPMDraft(ontologyId, true);

  if (isLoading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <Spin size="large" />
        <span className="ml-2">加载本体数据中...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-64 flex items-center justify-center text-red-500">
        <span>加载本体数据失败: {error.message}</span>
      </div>
    );
  }

  if (!draftResponse?.data?.graph) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500">
        <Empty description="暂无本体数据" />
      </div>
    );
  }

  // 转换OPM数据为知识图谱数据格式
  // 根据graph类型获取nodes和edges
  const graph = draftResponse.data.graph;
  const isOPMGraphData = 'nodes' in graph && 'edges' in graph;
  const graphData = convertOPMToKnowledgeGraph({
    nodes: isOPMGraphData ? graph.nodes || [] : [],
    edges: isOPMGraphData ? graph.edges || [] : [],
  });

  return (
    <div className="h-96 w-full border border-gray-200 rounded">
      <KnowledgeGraph externalData={graphData} debug={false} />
    </div>
  );
};
